import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2, LogIn, Star } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

// Kicks off Stripe checkout using the mobile-app backend's `/checkout/session`
// endpoint. Same endpoint the mobile app uses — so both platforms create
// transactions against the same Stripe customer.
export async function startCheckout(api, planId) {
  const { data } = await api.post("/checkout/session", {
    plan_id: planId,
    platform: "web",
    origin: window.location.origin,
  });
  window.location.href = data.url;
}

const campers = [
  {
    name: "Camper",
    tag: "Basic features",
    period: "Free for life",
    price: "RM0",
    perks: [
      "Digital passport",
      "Unlimited stamp collection",
      "Basic explore feed",
      "Save trip destinations",
    ],
    // Free tier — kick off Google sign-in.
    cta: "sign_in",
  },
  {
    name: "Adventurer",
    tag: "Pay-as-you-go",
    period: "per 7 days",
    price: "RM4.90",
    perks: [
      "Everything in Camper",
      "7-day premium access",
      "Detailed adventure stats",
      "Ad-free experience",
    ],
    lookup_key: "adventure_pass",
  },
  {
    name: "Explorer Annual",
    tag: "Best value",
    period: "per year",
    price: "RM24",
    highlight: true,
    perks: [
      "Everything in Adventurer",
      "Full year of premium",
      "Full adventure map & journal",
      "Priority support",
      "Early feature access",
    ],
    lookup_key: "explorer_annual",
  },
  {
    name: "Lifetime Explorer",
    tag: "One-time",
    period: "one payment",
    price: "RM240",
    perks: [
      "Everything in Explorer Annual",
      "Lifetime premium access",
      "Annual passport printout*",
      "Founding member badge",
    ],
    lookup_key: "lifetime",
  },
];

const owners = [
  {
    name: "Campsite Owner",
    tag: "Basic features",
    period: "Free for life",
    price: "RM0",
    perks: [
      "Listing on Explore feed",
      "Custom QR check-in",
      "Basic visitor overview",
    ],
    cta: "sign_in",
  },
  {
    name: "Adventure Business Pass",
    tag: "Pay-as-you-go",
    period: "per 7 days",
    price: "RM4.90",
    perks: [
      "Everything in Owner",
      "7-day premium visibility",
      "Boost featured placement",
      "Seasonal / event stamps",
    ],
    lookup_key: "business_pass",
  },
  {
    name: "Explorer Business",
    tag: "Recurring",
    period: "per year",
    price: "RM24",
    highlight: true,
    perks: [
      "Everything in Business Pass",
      "Unlimited stamps",
      "Visitor analytics dashboard",
      "Featured Explore placement",
      "Promotions & campaigns",
    ],
    lookup_key: "explorer_business",
  },
];

function PlanCard({ plan, i }) {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const isFree = plan.cta === "sign_in";

  const testId = `plan-${plan.name.toLowerCase().replace(/\s+/g, "-")}`;
  const buttonTestId = `plan-cta-${plan.lookup_key || plan.cta}`;

  const handleClick = async () => {
    // Free tier — send them to the shared /login page.
    if (isFree) {
      if (user) {
        toast.success(`You're signed in as ${user.name?.split(" ")[0] || "friend"}. Welcome!`);
        return;
      }
      navigate("/login?next=/passport");
      return;
    }

    // Any paid plan for a signed-out visitor → send them to sign-in first,
    // then resume checkout on landing back on /pricing?resume=<lookup_key>.
    if (!user) {
      const next = encodeURIComponent(`/pricing?resume=${plan.lookup_key}`);
      navigate(`/login?next=${next}`);
      return;
    }

    setLoading(true);
    try {
      await startCheckout(api, plan.lookup_key);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not start checkout.";
      toast.error(detail);
      setLoading(false);
    }
  };

  // ── Button rendering ──────────────────────────────────────────────────────
  let button;
  if (isFree) {
    button = (
      <button
        onClick={handleClick}
        className="btn btn-green mt-8 justify-center w-full"
        data-testid={buttonTestId}
      >
        <LogIn className="w-4 h-4" />
        {user ? "You're signed in" : "Sign in with Google"}
      </button>
    );
  } else {
    // Any paid plan — direct Stripe checkout (self-healed by backend).
    button = (
      <button
        onClick={handleClick}
        disabled={loading}
        className={`btn ${plan.highlight ? "btn-amber" : "btn-green"} mt-8 justify-center w-full`}
        data-testid={buttonTestId}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
          </>
        ) : !user ? (
          <>
            <LogIn className="w-4 h-4" /> Sign in to subscribe
          </>
        ) : (
          <>
            Subscribe · {plan.price} <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>
    );
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: i * 0.06 }}
      className="card-elev relative flex flex-col"
      style={
        plan.highlight
          ? { boxShadow: "0 20px 40px -10px rgba(217,119,6,0.35)", borderColor: "var(--premium)" }
          : {}
      }
      data-testid={testId}
    >
      {plan.highlight && (
        <span className="absolute -top-3 right-6 inline-flex items-center gap-1 bg-[#D97706] text-white text-[10px] font-extrabold tracking-widest px-3 py-1 rounded-full uppercase">
          <Star className="w-3 h-3" /> Popular
        </span>
      )}
      <div className="text-sm font-bold uppercase tracking-widest" style={{ color: "var(--brand)" }}>
        {plan.name}
      </div>
      <div className="text-xs mt-1 mb-3" style={{ color: "var(--on-surface-muted)" }}>
        {plan.tag}
      </div>
      <div className="flex items-end gap-2">
        <div className="text-[30px] font-extrabold" style={{ color: "var(--premium)" }}>
          {plan.price}
        </div>
        <div className="text-sm mb-1.5" style={{ color: "var(--on-surface-muted)" }}>
          {plan.period}
        </div>
      </div>
      <ul className="mt-6 space-y-3 flex-1">
        {plan.perks.map((p) => (
          <li key={p} className="flex items-start gap-3 text-sm">
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0"
              style={{
                background: "color-mix(in srgb, var(--brand) 15%, transparent)",
                color: "var(--brand)",
              }}
            >
              <Check className="w-3 h-3" strokeWidth={3} />
            </span>
            <span style={{ color: "var(--on-surface)" }}>{p}</span>
          </li>
        ))}
      </ul>
      {button}
    </motion.article>
  );
}

export default function Pricing() {
  const { user, loading: authLoading, api } = useAuth();
  const resumed = useRef(false);

  // If we returned from Google Sign-in with a resume=<lookup_key> flag, and
  // the user is now signed in (and not already premium), continue straight
  // to Stripe checkout for that plan.
  useEffect(() => {
    if (resumed.current) return;
    if (authLoading) return;

    const url = new URL(window.location.href);
    const resume = url.searchParams.get("resume");
    if (!resume) return;

    // Consume the intent + query param so a refresh doesn't loop the checkout.
    resumed.current = true;
    sessionStorage.removeItem("postAuthAction");
    url.searchParams.delete("resume");
    window.history.replaceState({}, "", url.pathname + (url.search ? url.search : "") + url.hash);

    if (!user) {
      toast.error("Please sign in to continue.");
      return;
    }
    if (resume === "signin") {
      toast.success(`Welcome, ${user.name?.split(" ")[0] || "friend"}!`);
      return;
    }

    toast.info("Redirecting you to secure checkout…");
    startCheckout(api, resume).catch((e) => {
      const detail = e?.response?.data?.detail || "Could not start checkout.";
      toast.error(detail);
    });
  }, [authLoading, user, api]);

  return (
    <>
      <section
        className="pt-[120px] md:pt-[140px] pb-10 text-center"
        style={{ background: "var(--surface-secondary)" }}
      >
        <div className="wrap">
          <div className="eyebrow mb-4">Pricing</div>
          <h1 className="text-3xl md:text-5xl font-extrabold mb-4" style={{ color: "var(--on-surface)" }}>
            Simple plans for every adventurer.
          </h1>
          <p className="body-lg max-w-2xl mx-auto">
            Start free forever. Upgrade to premium for detailed stats, adventure
            map, journaling and an ad-free experience.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="wrap">
          <div className="eyebrow mb-6">For Campers</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {campers.map((p, i) => (
              <PlanCard plan={p} i={i} key={p.name} />
            ))}
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="wrap">
          <div className="eyebrow mb-6">For Campsite Owners</div>
          <div className="grid md:grid-cols-3 gap-6">
            {owners.map((p, i) => (
              <PlanCard plan={p} i={i} key={p.name} />
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="wrap">
          <div
            className="p-6 md:p-8 rounded-2xl text-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
            style={{
              background: "var(--surface-secondary)",
              border: "1px solid var(--border-c)",
              color: "var(--on-surface-muted)",
            }}
          >
            <div>
              Prices in <strong style={{ color: "var(--on-surface)" }}>MYR</strong>. Free plans last forever.
            </div>
            <a href="/refund" className="font-bold" style={{ color: "var(--brand)" }}>
              Read our refund policy →
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
